import React, { useState, useCallback, useMemo } from 'react';

type PackageManagerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: Record<string, string>;
  onUpdatePackageJson: (content: string) => void;
};

type PackageInfo = {
  name: string;
  version: string;
  description?: string;
  'dist-tags'?: { latest: string };
};

type OutdatedPackage = {
  name: string;
  currentVersion: string;
  latestVersion: string;
  type: 'prod' | 'dev';
};

export const PackageManagerModal = React.memo(function PackageManagerModal({
  isOpen,
  onClose,
  files,
  onUpdatePackageJson,
}: PackageManagerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PackageInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingPackage, setAddingPackage] = useState<string | null>(null);
  const [removingPackage, setRemovingPackage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'packages' | 'updates'>('packages');
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [outdatedPackages, setOutdatedPackages] = useState<OutdatedPackage[]>([]);
  const [updatingPackage, setUpdatingPackage] = useState<string | null>(null);

  // Parse current package.json
  const packageJson = useMemo(() => {
    const content = files['package.json'] || files['/package.json'];
    if (!content) return null;
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }, [files]);

  // Get current dependencies
  const currentDeps = useMemo(() => {
    if (!packageJson) return [];
    const deps: { name: string; version: string; type: 'prod' | 'dev' }[] = [];

    if (packageJson.dependencies) {
      Object.entries(packageJson.dependencies).forEach(([name, version]) => {
        deps.push({ name, version: version as string, type: 'prod' });
      });
    }
    if (packageJson.devDependencies) {
      Object.entries(packageJson.devDependencies).forEach(([name, version]) => {
        deps.push({ name, version: version as string, type: 'dev' });
      });
    }

    return deps.sort((a, b) => a.name.localeCompare(b.name));
  }, [packageJson]);

  // Parse version string to get numeric version
  const parseVersion = useCallback((versionStr: string): string => {
    // Remove ^, ~, >=, etc. prefixes
    return versionStr.replace(/^[\^~>=<]+/, '');
  }, []);

  // Compare semver versions
  const isNewerVersion = useCallback((current: string, latest: string): boolean => {
    const currentParts = parseVersion(current).split('.').map(Number);
    const latestParts = latest.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
      const c = currentParts[i] || 0;
      const l = latestParts[i] || 0;
      if (l > c) return true;
      if (l < c) return false;
    }
    return false;
  }, [parseVersion]);

  // Check for updates
  const handleCheckUpdates = useCallback(async () => {
    if (currentDeps.length === 0) return;

    setCheckingUpdates(true);
    setError(null);
    setOutdatedPackages([]);

    const outdated: OutdatedPackage[] = [];

    try {
      // Check each package in parallel (with batching to avoid rate limiting)
      const batchSize = 5;
      for (let i = 0; i < currentDeps.length; i += batchSize) {
        const batch = currentDeps.slice(i, i + batchSize);

        const results = await Promise.allSettled(
          batch.map(async (dep) => {
            const response = await fetch(`https://registry.npmjs.org/${encodeURIComponent(dep.name)}/latest`);
            if (!response.ok) return null;
            const data = await response.json();
            return {
              name: dep.name,
              currentVersion: dep.version,
              latestVersion: data.version,
              type: dep.type,
            };
          })
        );

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            const pkg = result.value;
            if (isNewerVersion(pkg.currentVersion, pkg.latestVersion)) {
              outdated.push(pkg);
            }
          }
        });
      }

      setOutdatedPackages(outdated);
      if (outdated.length === 0) {
        setError('All packages are up to date!');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check for updates');
    } finally {
      setCheckingUpdates(false);
    }
  }, [currentDeps, isNewerVersion]);

  // Update a single package to latest version
  const handleUpdatePackage = useCallback(async (packageName: string, isDev: boolean) => {
    if (!packageJson) return;

    setUpdatingPackage(packageName);
    setError(null);

    try {
      const response = await fetch(`https://registry.npmjs.org/${encodeURIComponent(packageName)}/latest`);
      if (!response.ok) {
        throw new Error(`Failed to fetch latest version of ${packageName}`);
      }

      const data = await response.json();
      const newVersion = `^${data.version}`;

      const updatedPkg = { ...packageJson };
      if (isDev && updatedPkg.devDependencies) {
        updatedPkg.devDependencies = {
          ...updatedPkg.devDependencies,
          [packageName]: newVersion,
        };
      } else if (updatedPkg.dependencies) {
        updatedPkg.dependencies = {
          ...updatedPkg.dependencies,
          [packageName]: newVersion,
        };
      }

      onUpdatePackageJson(JSON.stringify(updatedPkg, null, 2));

      // Remove from outdated list
      setOutdatedPackages(prev => prev.filter(p => p.name !== packageName));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update package');
    } finally {
      setUpdatingPackage(null);
    }
  }, [packageJson, onUpdatePackageJson]);

  // Update all outdated packages
  const handleUpdateAll = useCallback(async () => {
    if (!packageJson || outdatedPackages.length === 0) return;

    setCheckingUpdates(true);
    setError(null);

    try {
      const updatedPkg = { ...packageJson };

      for (const pkg of outdatedPackages) {
        const response = await fetch(`https://registry.npmjs.org/${encodeURIComponent(pkg.name)}/latest`);
        if (!response.ok) continue;

        const data = await response.json();
        const newVersion = `^${data.version}`;

        if (pkg.type === 'dev' && updatedPkg.devDependencies) {
          updatedPkg.devDependencies[pkg.name] = newVersion;
        } else if (updatedPkg.dependencies) {
          updatedPkg.dependencies[pkg.name] = newVersion;
        }
      }

      onUpdatePackageJson(JSON.stringify(updatedPkg, null, 2));
      setOutdatedPackages([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update packages');
    } finally {
      setCheckingUpdates(false);
    }
  }, [packageJson, outdatedPackages, onUpdatePackageJson]);

  // Search npm registry
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setError(null);
    setSearchResults([]);

    try {
      const response = await fetch(
        `https://registry.npmjs.org/-/v1/search?text=${encodeURIComponent(searchQuery)}&size=10`
      );

      if (!response.ok) {
        throw new Error('Failed to search npm registry');
      }

      const data = await response.json();
      const results: PackageInfo[] = data.objects.map((obj: { package: PackageInfo }) => obj.package);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Add package
  const handleAddPackage = useCallback(async (packageName: string, isDev: boolean) => {
    if (!packageJson) return;

    setAddingPackage(packageName);
    setError(null);

    try {
      // Fetch latest version
      const response = await fetch(`https://registry.npmjs.org/${encodeURIComponent(packageName)}/latest`);
      if (!response.ok) {
        throw new Error(`Package "${packageName}" not found`);
      }

      const data = await response.json();
      const version = `^${data.version}`;

      // Update package.json
      const updatedPkg = { ...packageJson };
      if (isDev) {
        updatedPkg.devDependencies = {
          ...updatedPkg.devDependencies,
          [packageName]: version,
        };
        // Sort devDependencies
        const sorted = Object.keys(updatedPkg.devDependencies).sort();
        const sortedDevDeps: Record<string, string> = {};
        sorted.forEach(key => {
          sortedDevDeps[key] = updatedPkg.devDependencies[key];
        });
        updatedPkg.devDependencies = sortedDevDeps;
      } else {
        updatedPkg.dependencies = {
          ...updatedPkg.dependencies,
          [packageName]: version,
        };
        // Sort dependencies
        const sorted = Object.keys(updatedPkg.dependencies).sort();
        const sortedDeps: Record<string, string> = {};
        sorted.forEach(key => {
          sortedDeps[key] = updatedPkg.dependencies[key];
        });
        updatedPkg.dependencies = sortedDeps;
      }

      onUpdatePackageJson(JSON.stringify(updatedPkg, null, 2));
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add package');
    } finally {
      setAddingPackage(null);
    }
  }, [packageJson, onUpdatePackageJson]);

  // Remove package
  const handleRemovePackage = useCallback((packageName: string) => {
    if (!packageJson) return;

    setRemovingPackage(packageName);

    try {
      const updatedPkg = { ...packageJson };

      if (updatedPkg.dependencies?.[packageName]) {
        const { [packageName]: _, ...rest } = updatedPkg.dependencies;
        updatedPkg.dependencies = rest;
      }

      if (updatedPkg.devDependencies?.[packageName]) {
        const { [packageName]: _, ...rest } = updatedPkg.devDependencies;
        updatedPkg.devDependencies = rest;
      }

      onUpdatePackageJson(JSON.stringify(updatedPkg, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove package');
    } finally {
      setRemovingPackage(null);
    }
  }, [packageJson, onUpdatePackageJson]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    setOutdatedPackages([]);
    setActiveTab('packages');
    onClose();
  }, [onClose]);

  // Check if package is already installed
  const isInstalled = useCallback((packageName: string) => {
    return currentDeps.some(dep => dep.name === packageName);
  }, [currentDeps]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal package-manager-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '650px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div className="modal-title">Package Manager</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {!packageJson ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
              No package.json found in the project
            </div>
          ) : (
            <>
              {/* Tab buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                <button
                  className="pill-btn"
                  onClick={() => setActiveTab('packages')}
                  style={{
                    background: activeTab === 'packages' ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: activeTab === 'packages' ? 'white' : 'var(--text)',
                  }}
                >
                  Packages
                </button>
                <button
                  className="pill-btn"
                  onClick={() => setActiveTab('updates')}
                  style={{
                    background: activeTab === 'updates' ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: activeTab === 'updates' ? 'white' : 'var(--text)',
                  }}
                >
                  Updates {outdatedPackages.length > 0 && `(${outdatedPackages.length})`}
                </button>
              </div>

              {error && (
                <div style={{
                  padding: '8px 12px',
                  background: error.includes('up to date') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255, 100, 100, 0.1)',
                  border: `1px solid ${error.includes('up to date') ? 'var(--success)' : 'var(--error)'}`,
                  borderRadius: '4px',
                  marginBottom: '16px',
                  color: error.includes('up to date') ? 'var(--success)' : 'var(--error)',
                  fontSize: '12px',
                }}>
                  {error}
                </div>
              )}

              {activeTab === 'packages' && (
                <>
                  {/* Search */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                      Search npm packages:
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g., lodash, axios, react-query..."
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          color: 'var(--text)',
                          fontSize: '13px',
                        }}
                      />
                      <button
                        className="pill-btn"
                        onClick={handleSearch}
                        disabled={searching || !searchQuery.trim()}
                        style={{ opacity: searching || !searchQuery.trim() ? 0.5 : 1 }}
                      >
                        {searching ? 'Searching...' : 'Search'}
                      </button>
                    </div>
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                        Search Results:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {searchResults.map((pkg) => {
                          const installed = isInstalled(pkg.name);
                          const adding = addingPackage === pkg.name;

                          return (
                            <div
                              key={pkg.name}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 12px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '4px',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{pkg.name}</div>
                                {pkg.description && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'var(--muted)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {pkg.description}
                                  </div>
                                )}
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                                v{pkg.version}
                              </span>
                              {installed ? (
                                <span style={{
                                  fontSize: '11px',
                                  color: 'var(--success)',
                                  padding: '4px 8px',
                                  background: 'rgba(34, 197, 94, 0.1)',
                                  borderRadius: '4px',
                                }}>
                                  Installed
                                </span>
                              ) : (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button
                                    className="pill-btn"
                                    onClick={() => handleAddPackage(pkg.name, false)}
                                    disabled={adding}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    {adding ? '...' : 'Add'}
                                  </button>
                                  <button
                                    className="pill-btn"
                                    onClick={() => handleAddPackage(pkg.name, true)}
                                    disabled={adding}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                    title="Add as dev dependency"
                                  >
                                    {adding ? '...' : 'Dev'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Installed Packages */}
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                      Installed Packages ({currentDeps.length}):
                    </div>
                    {currentDeps.length === 0 ? (
                      <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                        No packages installed
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '300px', overflow: 'auto' }}>
                        {currentDeps.map((dep) => {
                          const removing = removingPackage === dep.name;

                          return (
                            <div
                              key={dep.name}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <span
                                style={{
                                  padding: '2px 6px',
                                  background: dep.type === 'dev' ? 'var(--warning)' : 'var(--accent)',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                }}
                              >
                                {dep.type === 'dev' ? 'DEV' : 'PROD'}
                              </span>
                              <span style={{ flex: 1, fontWeight: 500 }}>{dep.name}</span>
                              <span style={{ color: 'var(--muted)' }}>{dep.version}</span>
                              <button
                                className="pill-btn"
                                onClick={() => handleRemovePackage(dep.name)}
                                disabled={removing}
                                style={{
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  color: 'var(--error)',
                                }}
                              >
                                {removing ? '...' : 'Remove'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'updates' && (
                <>
                  {/* Check for updates button */}
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                    <button
                      className="pill-btn"
                      onClick={handleCheckUpdates}
                      disabled={checkingUpdates || currentDeps.length === 0}
                      style={{
                        opacity: checkingUpdates || currentDeps.length === 0 ? 0.5 : 1,
                        background: 'var(--accent)',
                        color: 'white',
                      }}
                    >
                      {checkingUpdates ? 'Checking...' : 'Check for Updates'}
                    </button>
                    {outdatedPackages.length > 0 && (
                      <button
                        className="pill-btn"
                        onClick={handleUpdateAll}
                        disabled={checkingUpdates}
                        style={{
                          background: 'var(--success)',
                          color: 'white',
                        }}
                      >
                        Update All ({outdatedPackages.length})
                      </button>
                    )}
                  </div>

                  {/* Outdated packages list */}
                  {outdatedPackages.length > 0 ? (
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                        Outdated Packages ({outdatedPackages.length}):
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {outdatedPackages.map((pkg) => {
                          const updating = updatingPackage === pkg.name;

                          return (
                            <div
                              key={pkg.name}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                border: '1px solid var(--warning)',
                              }}
                            >
                              <span
                                style={{
                                  padding: '2px 6px',
                                  background: pkg.type === 'dev' ? 'var(--warning)' : 'var(--accent)',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                }}
                              >
                                {pkg.type === 'dev' ? 'DEV' : 'PROD'}
                              </span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{pkg.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                                  <span style={{ color: 'var(--error)' }}>{parseVersion(pkg.currentVersion)}</span>
                                  {' → '}
                                  <span style={{ color: 'var(--success)' }}>{pkg.latestVersion}</span>
                                </div>
                              </div>
                              <button
                                className="pill-btn"
                                onClick={() => handleUpdatePackage(pkg.name, pkg.type === 'dev')}
                                disabled={updating}
                                style={{
                                  background: 'var(--success)',
                                  color: 'white',
                                  fontSize: '11px',
                                  padding: '4px 12px',
                                }}
                              >
                                {updating ? 'Updating...' : 'Update'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : !checkingUpdates && (
                    <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
                      Click "Check for Updates" to scan for outdated packages
                    </div>
                  )}
                </>
              )}

              {/* Note */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'var(--bg-secondary)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'var(--muted)',
              }}>
                <strong>Note:</strong> This updates package.json only.
                Run <code style={{ background: 'var(--bg)', padding: '2px 4px', borderRadius: '2px' }}>npm install</code> in
                your terminal to install the packages.
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default PackageManagerModal;
